import React, { useState } from 'react';
import { ShoppingCart, Calendar, Users, Beef, CheckCircle2, AlertTriangle, Flame, ChevronDown, GripVertical, Milk, Wheat, Carrot } from 'lucide-react';
import TermsOfServiceModal from './TermsOfServiceModal';
import { getTranslation } from '../utils/translations';
import type { Language } from '../types';
import { detectBrowserLanguage } from '../utils/appLanguage';

interface LandingPageProps {
  onLoginClick: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick }) => {
  const [isTosOpen, setIsTosOpen] = useState(false);
  const browserLang = detectBrowserLanguage();
  const t = (key: any) => getTranslation(browserLang, key);
  return (
    <div className="min-h-screen bg-primary-bg font-rubik text-slate-800 dark:text-slate-100 flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Gradient Overlay */}
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=2000&q=80")' }}
        >
          {/* Gradient Overlay: Deep Navy/Slate */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/90 via-primary-dark/70 to-primary-dark/30 dark:from-slate-950/95 dark:via-slate-900/80 dark:to-slate-900/40" />
        </div>

        <div className="relative z-10 container mx-auto px-6 py-12 flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-8">
          
          {/* Left Column: Typography & Action */}
          <div className="flex-1 text-center lg:text-left text-white max-w-2xl">
            <h1 className="font-nunito text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 animate-fade-in-down">
              Cook like a chef.<br />
              Plan like a pro.<br />
              <span className="text-highlight-icon">Stop wasting food.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-10 font-medium max-w-xl mx-auto lg:mx-0">
              A premium household command center that syncs your budget, your butcher, and your family.
            </p>
            <button 
              onClick={onLoginClick}
              className="bg-action-primary hover:bg-[#234534] text-white font-bold py-4 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-lg"
            >
              Join the Beta
            </button>
          </div>

          {/* Right Column: Floating UI Elements */}
          <div className="flex-1 relative w-full max-w-lg h-[400px] hidden lg:block">
            {/* Top Right Card: Active Cart Categories */}
            <div className="absolute top-4 right-4 lg:right-12 glass-panel rounded-3xl p-5 w-80 transform rotate-2 hover:rotate-0 transition-transform duration-300 z-20">
              <div className="flex items-center gap-3 mb-5">
                <div className="bg-action-primary/10 p-2.5 rounded-full">
                  <ShoppingCart className="w-5 h-5 text-action-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-slate-100 text-lg leading-none">Active Cart</h3>
                  <p className="text-[10px] text-slate-400 font-bold tracking-widest mt-1 uppercase">17 Items</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Meat Category */}
                <div className="flex items-center justify-between border border-red-100 dark:border-red-900/30 bg-red-50/40 dark:bg-red-900/10 rounded-full px-4 py-3">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-slate-300" />
                    <Flame className="w-5 h-5 text-[#E63946]" />
                    <span className="font-bold text-[#E63946] tracking-widest text-xs">MEAT</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-200/60 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold px-2.5 py-0.5 rounded-full">1</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </div>
                </div>

                {/* Dairy Category */}
                <div className="flex items-center justify-between border border-blue-100 dark:border-blue-900/30 bg-blue-50/40 dark:bg-blue-900/10 rounded-full px-4 py-3">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-slate-300" />
                    <Milk className="w-5 h-5 text-[#4361EE]" />
                    <span className="font-bold text-[#4361EE] tracking-widest text-xs">DAIRY</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-200/60 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold px-2.5 py-0.5 rounded-full">3</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </div>
                </div>

                {/* Pantry Category */}
                <div className="flex items-center justify-between border border-amber-100 dark:border-amber-900/30 bg-amber-50/40 dark:bg-amber-900/10 rounded-full px-4 py-3">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-slate-300" />
                    <Wheat className="w-5 h-5 text-[#B07D62]" />
                    <span className="font-bold text-[#B07D62] tracking-widest text-xs">PANTRY</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-200/60 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold px-2.5 py-0.5 rounded-full">3</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </div>
                </div>

                {/* Vegetables Category */}
                <div className="flex items-center justify-between border border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/40 dark:bg-emerald-900/10 rounded-full px-4 py-3">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-slate-300" />
                    <Carrot className="w-5 h-5 text-[#2A9D8F]" />
                    <span className="font-bold text-[#2A9D8F] tracking-widest text-xs">VEGETABLES</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-200/60 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold px-2.5 py-0.5 rounded-full">7</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Left Card: Defrosting Reminders */}
            <div className="absolute bottom-12 left-4 lg:left-0 bg-accent-warm dark:bg-slate-800/90 backdrop-blur-md rounded-2xl p-5 w-80 shadow-2xl border border-white/50 dark:border-slate-700 transform -rotate-3 hover:rotate-0 transition-transform duration-300 z-30">
              <div className="flex items-start gap-4">
                <div className="bg-highlight-icon/10 p-3 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-highlight-icon" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 dark:text-slate-100 mb-1">Defrosting Reminder</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug">
                    <strong className="text-slate-800 dark:text-slate-100">Friday Night Asado:</strong><br />
                    Move Prime Brisket to the fridge today.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Grid */}
      <section className="py-20 px-6 bg-primary-bg dark:bg-slate-950">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="font-nunito text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              Why MealPai?
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              We focus on premium quality, lowering waste, and treating your ingredients with respect.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Card 1: Precision Procurement */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 border border-slate-100 dark:border-slate-800">
              <div className="w-14 h-14 bg-action-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <ShoppingCart className="w-7 h-7 text-action-primary" />
              </div>
              <h3 className="font-nunito text-2xl font-bold mb-3 text-slate-800 dark:text-slate-100">
                Precision Procurement
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Buy exactly what you need with an active cart that automatically organizes by categories (like Meat and General Food). Lower waste and keep your budget in check.
              </p>
            </div>

            {/* Card 2: Event-Driven Planning */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 border border-slate-100 dark:border-slate-800">
              <div className="w-14 h-14 bg-highlight-icon/10 rounded-2xl flex items-center justify-center mb-6">
                <Calendar className="w-7 h-7 text-highlight-icon" />
              </div>
              <h3 className="font-nunito text-2xl font-bold mb-3 text-slate-800 dark:text-slate-100">
                Event-Driven Planning
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Orchestrate an event. From a "Friday Night Asado" to daily dinners, know exactly when to buy, age, and prep.
              </p>
            </div>

            {/* Card 3: Household Command */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 border border-slate-100 dark:border-slate-800">
              <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="font-nunito text-2xl font-bold mb-3 text-slate-800 dark:text-slate-100">
                Household Command
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                A shared brain for the kitchen. Assign roles like "Chef" and "Procurement Officer" to family members.
              </p>
            </div>

            {/* Card 4: Butcher-Grade Logic */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 border border-slate-100 dark:border-slate-800">
              <div className="w-14 h-14 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6">
                <Beef className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="font-nunito text-2xl font-bold mb-3 text-slate-800 dark:text-slate-100">
                Butcher-Grade Logic
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Treat high-end cuts with respect. Incorporates deep knowledge of meat sourcing, specific cuts, and preparation techniques rather than treating ingredients as basic commodities.
              </p>
            </div>

          </div>
        </div>
      </section>
      
      {/* Footer minimal */}
      <footer className="py-8 text-center text-slate-500 dark:text-slate-400 text-sm">
        <div className="flex flex-col items-center gap-2">
          <p>&copy; {new Date().getFullYear()} MealPai. All rights reserved.</p>
          <button
            type="button"
            onClick={() => setIsTosOpen(true)}
            className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 underline underline-offset-4"
          >
            {t('termsOfService')}
          </button>
        </div>
      </footer>

      <TermsOfServiceModal
        isOpen={isTosOpen}
        onClose={() => setIsTosOpen(false)}
        language={browserLang}
      />
    </div>
  );
};

export default LandingPage;
